/**
 * Extract required fields from the token payload, honoring both SDK variants:
 *   - Modern: accountTokens[0].{accessToken, refreshToken, account.accountId}
 *   - Legacy: tokenPayload.{accessToken, refreshToken} at the top level
 *
 * Throws with a descriptive message listing ALL missing fields when any
 * required field is absent.
 */
function extractRequiredFields(tokenPayload) {
    const firstAccountToken = tokenPayload.accountTokens?.[0];
    const accessToken = firstAccountToken?.accessToken ?? tokenPayload.accessToken;
    const brokerType = tokenPayload.brokerType ? String(tokenPayload.brokerType) : undefined;
    const accountId = firstAccountToken?.account?.accountId;
    if (!accessToken || !brokerType || !accountId) {
        const missing = [!accessToken && "accessToken", !brokerType && "brokerType", !accountId && "accountId"]
            .filter(Boolean)
            .join(", ");
        throw new Error(`Missing required fields in connection payload: ${missing}`);
    }
    // Some brokers return a single bearer token without a separate refresh token.
    // Fall back to accountTokens[0].refreshToken → top-level refreshToken → the
    // access token so the refresh slot is always populated; expiry logic still
    // drives re-authentication based on refreshTokenExpiresAt.
    const refreshToken = firstAccountToken?.refreshToken ?? tokenPayload.refreshToken ?? accessToken;
    return { accessToken, refreshToken, brokerType, accountId };
}
/**
 * Extract and validate required fields from a Mesh SDK LinkPayload.
 * Throws with a descriptive message if required fields are missing.
 *
 * Supports two SDK payload variants:
 *   1) Modern: `tokenPayload.accountTokens[0].{accessToken, refreshToken, account.accountId}`
 *   2) Legacy: `tokenPayload.{accessToken, refreshToken}` at the top level,
 *      with `accountTokens[0].account.accountId` still used for the account id.
 *
 * The pre-migration mobile provider handled both variants; preserving the
 * fallback avoids a compatibility regression across SDK versions/brokers.
 */
export function parseConnectionPayload(payload) {
    const tokenPayload = payload.accessToken;
    if (!tokenPayload)
        throw new Error("No access token in connection payload");
    const { accessToken, refreshToken, brokerType, accountId } = extractRequiredFields(tokenPayload);
    return {
        accessToken,
        refreshToken,
        brokerType,
        // brokerName is a display label; the wire field may be missing on some
        // brokers, so we use brokerType as a legible surrogate.
        brokerName: tokenPayload.brokerName ?? brokerType,
        accountId,
        // integrationId is used as a stable per-broker identifier in analytics and
        // storage. Older SDK versions don't surface it at the top level — brokerType
        // is a non-colliding fallback since there's one integration per broker.
        integrationId: payload.integrationId ?? brokerType,
        // Mesh API documents 1 hour as the default access token lifetime when
        // expiresInSeconds is not returned. Matches the pre-migration mobile/web code.
        expiresInSeconds: tokenPayload.expiresInSeconds ?? 3600,
        // Mesh API documents 30 days as the default refresh token lifetime when
        // refreshTokenExpiresInSeconds is not returned. Matches pre-migration code.
        refreshTokenExpiresInSeconds: tokenPayload.refreshTokenExpiresInSeconds ?? 30 * 24 * 3600,
        brokerLogoUrl: tokenPayload.brokerBrandInfo?.iconDarkUrl ?? tokenPayload.brokerBrandInfo?.iconLightUrl,
    };
}
